import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Calendar, DollarSign, Target } from 'lucide-react';

const DetailedView = ({ investments, visibleFields, currency }) => {
  const navigate = useNavigate();

  const getStageColor = (stage) => {
    const colors = {
      'Pipeline': 'bg-gray-100 text-gray-800',
      'Active': 'bg-green-100 text-green-800',
      'Under Review': 'bg-yellow-100 text-yellow-800',
      'Hold': 'bg-orange-100 text-orange-800',
      'Watchlist': 'bg-purple-100 text-purple-800',
      'Exited': 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  const formatMultiple = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${value.toFixed(2)}x`;
  };

  const renderFieldValue = (investment, field) => {
    // Check if it's a metric field
    const metricFields = ['total_investment', 'total_realisation', 'current_stake_value', 'current_shareholding', 'multiple', 'irr'];
    if (metricFields.includes(field)) {
      const value = investment.metrics?.[field];
      
      switch (field) {
        case 'total_investment':
        case 'total_realisation':
        case 'current_stake_value':
          return formatCurrency(value);
        case 'current_shareholding':
        case 'irr':
          return formatPercentage(value);
        case 'multiple':
          return formatMultiple(value);
        default:
          return value || 'N/A';
      }
    }

    // Handle regular fields
    switch (field) {
      case 'portfolio_company_name':
        return investment.portfolio_company_name;
      case 'investment_stage':
        return (
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStageColor(investment.investment_stage)}`}>
            {investment.investment_stage || 'Pipeline'}
          </span>
        );
      case 'sector':
        return investment.sector || 'N/A';
      case 'investment_currency':
        return investment.investment_currency;
      case 'types_of_securities_offered':
        return investment.types_of_securities_offered?.length > 0
          ? investment.types_of_securities_offered.join(', ')
          : 'N/A';
      case 'created_at':
        return new Date(investment.created_at).toLocaleDateString();
      default:
        return investment[field] || 'N/A';
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      'portfolio_company_name': 'Company Name',
      'investment_stage': 'Stage',
      'sector': 'Sector',
      'investment_currency': 'Currency',
      'types_of_securities_offered': 'Securities',
      'total_investment': 'Total Investment',
      'total_realisation': 'Total Realisation',
      'current_stake_value': 'Current Stake Value',
      'current_shareholding': '% Shareholding',
      'multiple': 'Multiple (MOIC)',
      'irr': 'IRR %',
      'created_at': 'Created Date'
    };
    return labels[field] || field;
  };

  const getFieldIcon = (field) => {
    switch (field) {
      case 'investment_stage':
        return <Target size={16} className="text-indigo-600" />;
      case 'investment_currency':
      case 'total_investment':
      case 'total_realisation':
      case 'current_stake_value':
        return <DollarSign size={16} className="text-green-600" />;
      case 'multiple':
      case 'irr':
      case 'current_shareholding':
        return <TrendingUp size={16} className="text-purple-600" />;
      case 'created_at':
        return <Calendar size={16} className="text-blue-600" />;
      default:
        return <TrendingUp size={16} className="text-gray-600" />;
    }
  };

  if (investments.length === 0) {
    return (
      <div className="text-center py-12">
        <TrendingUp size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">No investments found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {investments.map((investment) => (
        <div
          key={investment.id}
          onClick={() => navigate(`/investments/${investment.id}`)}
          className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer overflow-hidden border border-gray-200"
        >
          {/* Card Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4">
            <h3 className="text-lg font-bold text-white truncate">
              {investment.portfolio_company_name}
            </h3>
          </div>

          {/* Card Body */}
          <div className="p-4 space-y-3">
            {visibleFields
              .filter(field => field !== 'portfolio_company_name') // Company name is in header
              .map((field) => (
                <div key={field} className="flex items-start space-x-2">
                  <div className="mt-1">
                    {getFieldIcon(field)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      {getFieldLabel(field)}
                    </p>
                    <p className="text-sm font-medium text-gray-900 truncate mt-1">
                      {renderFieldValue(investment, field)}
                    </p>
                  </div>
                </div>
              ))}
          </div>

          {/* Card Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <button className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
              View Details →
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DetailedView;
